import React from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from 'recharts'
import { ChatBlock } from './chatTypes'

// High-contrast palette (chart sits on a teal bubble).
const SERIES_COLORS = ['#7FD1D3', '#FFB74D', '#A5D6A7', '#F48FB1', '#CE93D8']

interface ChatChartProps {
  block: ChatBlock
}

/** Shorten long asset names so the x-axis stays readable. */
function shortLabel(label: string): string {
  const s = String(label || '')
  return s.length > 14 ? `${s.slice(0, 13)}…` : s
}

/**
 * Renders a copilot `chart` block with recharts. The backend sends:
 *   { type: 'chart', chart_type: 'bar'|'line', x: string[],
 *     series: [{ name, points: (number|null)[] }], y_label?: string }
 * We pivot that into recharts' row-per-x-label shape.
 */
function ChatChart({ block }: ChatChartProps) {
  const { chart_type, x, series, y_label } = block

  if (!x || x.length === 0 || !series || series.length === 0) {
    return null
  }

  // Pivot column-oriented series into one row per x label:
  //   [{ label: 'CU-1', 'Last week': 900, 'This week': 973 }, ...]
  const data = x.map((label, i) => {
    const row: Record<string, string | number | null> = { label }
    for (const s of series) {
      row[s.name] = s.points[i] ?? null
    }
    return row
  })

  const isLine = (chart_type || 'bar').toLowerCase() === 'line'

  const axisTick = { fill: '#ffffff', fontSize: 11 }
  const axisLabelStyle = { fill: '#ffffff', fontSize: 11 }

  // Taller chart, and widen horizontally when there are many categories so the
  // bars/labels don't cram. The wrapper scrolls horizontally past the width.
  const height = 320
  const perCategory = 56
  const minWidth = 420
  const chartWidth = Math.max(minWidth, data.length * perCategory * Math.max(1, series.length / 2))

  return (
    <div className="chat-chart-wrapper">
      <div className="chat-chart-scroll" style={{ width: '100%', overflowX: 'auto' }}>
        <div style={{ width: chartWidth, maxWidth: '100%', minWidth }}>
          <ResponsiveContainer width="100%" height={height}>
            {isLine ? (
              <LineChart data={data} margin={{ top: 12, right: 16, bottom: 72, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.18)" />
                <XAxis
                  dataKey="label"
                  tick={axisTick}
                  interval={0}
                  angle={-40}
                  height={78}
                  textAnchor="end"
                  tickFormatter={shortLabel}
                />
                <YAxis tick={axisTick} width={48}>
                  {y_label ? (
                    <Label value={y_label} angle={-90} position="insideLeft" style={axisLabelStyle} />
                  ) : null}
                </YAxis>
                <Tooltip
                  contentStyle={{ background: '#0A1946', border: '1px solid #2A3F90', fontSize: 12 }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#fff' }} />
                {series.map((s, i) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            ) : (
              <BarChart data={data} margin={{ top: 12, right: 16, bottom: 72, left: 8 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.18)" />
                <XAxis
                  dataKey="label"
                  tick={axisTick}
                  interval={0}
                  angle={-40}
                  height={78}
                  textAnchor="end"
                  tickFormatter={shortLabel}
                />
                <YAxis tick={axisTick} width={48}>
                  {y_label ? (
                    <Label value={y_label} angle={-90} position="insideLeft" style={axisLabelStyle} />
                  ) : null}
                </YAxis>
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.1)' }}
                  contentStyle={{ background: '#0A1946', border: '1px solid #2A3F90', fontSize: 12 }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#fff' }} />
                {series.map((s, i) => (
                  <Bar
                    key={s.name}
                    dataKey={s.name}
                    fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                    maxBarSize={48}
                  />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

export default ChatChart